import React, { useEffect, useRef, useState } from "react";
import Icon from "../../../Elements/Icon/Icon.tsx";
import AnimationContainer from "../../../Reusable/AnimationContainer/AnimationContainer.js";
import bytesToDisplayHelper from "../../../../utils/bytesToDisplayHelper.js";
import Tooltip from "components/Elements/Tooltip/Tooltip.js";
import useLightboxOpenPreview from "../Helpers/useLightboxOpenPreview.js";
import VideoPreviewModal from "../Reusable/VideoPreviewModal.js";
import { getRelativePath } from "../Helpers/uploaderAPI.js";
import { useInView } from "react-intersection-observer";
import { useUploader } from "../../../../contexts/uploaderContext/uploaderContext.js";
import { CircularProgressbar } from "react-circular-progressbar";
import useFileProgress from "../Helpers/useFileProgress.js";
import UploadGalleryIcon from "./UploadGalleryIcon.js";
import useCreateThumbPreview from "../Helpers/useCreateThumbPreview.js";

const TooltipIconElem = ({ name, isComplete, isError, persentegeProgress }) => (
  <Tooltip tooltipText={name}>
    {isComplete ? (
      <Icon name="check" green />
    ) : isError ? (
      <Icon name="warning" red />
    ) : (
      <CircularProgressbar value={persentegeProgress} />
    )}
  </Tooltip>
);

const TooltipWithMemo = React.memo(TooltipIconElem, (prev, next) => {
  if (prev.isComplete !== next.isComplete) return false;
  if (prev.isError !== next.isError) return false;
  if (prev.persentegeProgress !== next.persentegeProgress) return false;
  return true;
});

export const UploadGalleryItem = ({
  file,
  size,
  itemsPerRow,
  progress,
  preview,
}) => {
  const { uploaderRemoveFile, getUploading, getIsUppy } = useUploader();
  const [hasThumbPreviewGeneratingError, setHasThumbPreviewGeneratingError] =
    useState(false);

  // different file-structure on different uploaders API
  const fileObj = getIsUppy() ? file : file.file;

  const [hover, setHover] = useState(false);

  const [inViewRef, inView] = useInView({ threshold: 0, rootMargin: "330px" });
  const hoverRef = useRef(null);

  const { handleOpenLightbox } = useLightboxOpenPreview(fileObj, preview);

  const pxSize = Math.floor(size.width / itemsPerRow) - 9 + "px";
  const fileSize = bytesToDisplayHelper(file.size);

  const thumbPreview = useCreateThumbPreview(fileObj, preview);

  const name = getRelativePath(fileObj)
    ? getRelativePath(fileObj) + "/" + file.name
    : file.name;

  const { isComplete, isError, persentegeProgress } = useFileProgress(
    fileObj,
    progress
  );

  return (
    <div
      className="file-preview-item"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      ref={inViewRef}
      style={{
        width: pxSize,
        height: pxSize,
      }}
    >
      {inView ? (
        <>
          <VideoPreviewModal
            hover={hover}
            preview={preview}
            fileType={fileObj.type}
            fileRef={hoverRef}
          />
          {!getUploading() ? (
            <AnimationContainer animationOn={hover} animationClassName="fade">
              <div
                className="hover-section"
                onClick={handleOpenLightbox}
                ref={hoverRef}
              >
                <div
                  className="close-icon"
                  onClick={(e) => {
                    uploaderRemoveFile(file.id);
                    e.stopPropagation();
                  }}
                >
                  <Icon name={"x"} />
                </div>
                <div className="info-section">
                  <Tooltip tooltipText={name}>
                    <div className="name">{name}</div>
                  </Tooltip>
                  <div className="size">{fileSize.size + fileSize.type}</div>
                </div>
              </div>
            </AnimationContainer>
          ) : (
            <div className="hover-section uploading">
              <TooltipWithMemo
                {...{ name, isComplete, isError, persentegeProgress }}
              />
            </div>
          )}
          <UploadGalleryIcon file={file} thumbPreview={thumbPreview} />
        </>
      ) : null}
    </div>
  );
};

export const UploadGalleryItemWithMemo = React.memo(UploadGalleryItem);
